import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback
} from 'react'
import {
  getCurrentUser,
  getUserAttributes,
  getJwtToken,
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  isAuthenticated as checkCognitoAuth
} from '../config/cognito'
import { getMfaStatus, sendMfaOtp, verifyMfaOtp } from '../services/mfaApi'

export interface User {
  username: string
  email?: string
  name?: string
  givenName?: string
  familyName?: string
  sub?: string
  [key: string]: string | undefined
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (username: string, password: string) => Promise<{ success: boolean; mfaRequired?: boolean }>
  logout: () => void
  isAuthenticated: boolean
  loading: boolean
  userProfileLoading: boolean
  mfaRequired: boolean
  completeMfaSignIn: (otp: string) => Promise<{ success: boolean; error?: string }>
  resendMfaOtp: () => Promise<void>
  cancelMfa: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const COGNITO_TOKEN_KEY = 'cognito_token'
const MFA_PENDING_KEY = 'wps_mfa_pending'
const MFA_SESSION_KEY = 'wps_mfa_session'
const MFA_PENDING_USER_KEY = 'wps_mfa_pending_user'
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

type PendingMfaUser = { username: string; attributes: Record<string, string> }

function persistMfaPending(session: string, pending: PendingMfaUser): void {
  sessionStorage.setItem(MFA_PENDING_KEY, '1')
  sessionStorage.setItem(MFA_SESSION_KEY, session)
  sessionStorage.setItem(MFA_PENDING_USER_KEY, JSON.stringify(pending))
}

function clearMfaPending(): void {
  sessionStorage.removeItem(MFA_PENDING_KEY)
  sessionStorage.removeItem(MFA_SESSION_KEY)
  sessionStorage.removeItem(MFA_PENDING_USER_KEY)
}

function readMfaPending(): { session: string; pendingUser: PendingMfaUser } | null {
  if (sessionStorage.getItem(MFA_PENDING_KEY) !== '1') return null
  const session = sessionStorage.getItem(MFA_SESSION_KEY)
  const raw = sessionStorage.getItem(MFA_PENDING_USER_KEY)
  if (!session || !raw) {
    clearMfaPending()
    return null
  }
  try {
    return { session, pendingUser: JSON.parse(raw) as PendingMfaUser }
  } catch {
    clearMfaPending()
    return null
  }
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length < 2) return null
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4)
    return JSON.parse(atob(padded))
  } catch {
    return null
  }
}

function looksLikeUserId(value: string, sub?: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) return false
  if (sub && trimmed === sub) return true
  return UUID_RE.test(trimmed)
}

function sanitizeDisplayCandidate(value: string, sub?: string): string {
  return looksLikeUserId(value, sub) ? '' : value
}

function userFromPayload(payload: Record<string, unknown>, username?: string): User {
  const p = payload as Record<string, string>
  const sub = p.sub || ''
  const email = p.email || ''
  const cognitoUsername = p['cognito:username'] || ''
  const preferredUsername = sanitizeDisplayCandidate(
    p.preferred_username || p.cognito_username || cognitoUsername || username || '',
    sub
  )
  const givenName = (p.given_name || '').trim()
  const familyName = (p.family_name || '').trim()
  const fullName =
    [givenName, familyName].filter(Boolean).join(' ') ||
    sanitizeDisplayCandidate(p.name || '', sub) ||
    sanitizeDisplayCandidate(p.nickname || '', sub) ||
    sanitizeDisplayCandidate((p as Record<string, string>)['custom:displayName'] || '', sub) ||
    ''
  const safeUsername = sanitizeDisplayCandidate(username || '', sub)
  return {
    username: preferredUsername || email || safeUsername || '',
    email,
    name: fullName || preferredUsername || safeUsername || '',
    givenName: givenName || undefined,
    familyName: familyName || undefined,
    sub
  }
}

/** Sidebar lines: prefer Cognito given/family; else split display name on first space. */
export function getUserNameLines(user: User | null): { primary: string; secondary?: string } {
  if (!user) return { primary: 'User' }
  if (user.givenName && user.familyName) {
    return { primary: user.givenName, secondary: user.familyName }
  }
  const display = getDisplayName(user)
  const space = display.indexOf(' ')
  if (space > 0) {
    return { primary: display.slice(0, space), secondary: display.slice(space + 1) }
  }
  return { primary: display }
}

/** Prefer name over email for display; if only email is set, show a friendly version (e.g. "vinay.baburao@crce.com" → "Vinay Baburao"). */
export function getDisplayName(user: User | null): string {
  if (!user) return 'User'
  const name = (user.name || '').trim()
  const email = (user.email || user.username || '').trim()
  if (name && !name.includes('@')) return name
  if (email && email.includes('@')) {
    const local = email.split('@')[0] || ''
    const friendly = local.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    if (friendly) return friendly
  }
  return name || email || 'User'
}

export function isUserProfileReady(user: User | null): boolean {
  if (!user) return false
  const email = (user.email || '').trim()
  if (!email.includes('@')) return false
  return getDisplayName(user) !== 'User'
}

async function enrichUserFromCognito(): Promise<{ user: User; token: string | null } | null> {
  const ok = await checkCognitoAuth()
  if (!ok) return null

  const currentUser = getCurrentUser()
  if (!currentUser) return null

  const attributes = await getUserAttributes(currentUser)
  const jwtToken = await getJwtToken()
  const payload = jwtToken ? decodeJwtPayload(jwtToken) : null
  const userData = userFromPayload(
    (attributes as Record<string, unknown>) || (payload || {}),
    currentUser.getUsername()
  )
  return { user: userData, token: jwtToken }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [userProfileLoading, setUserProfileLoading] = useState(true)
  const [mfaRequired, setMfaRequired] = useState(false)
  const [mfaSession, setMfaSession] = useState<string | null>(null)
  const [pendingUser, setPendingUser] = useState<{
    username: string
    attributes: Record<string, string>
  } | null>(null)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      const mfaPending = readMfaPending()
      if (mfaPending) {
        if (!cancelled) {
          setMfaSession(mfaPending.session)
          setMfaRequired(true)
          setPendingUser(mfaPending.pendingUser)
          setLoading(false)
          setUserProfileLoading(false)
        }
        return
      }

      let releasedApp = false

      const cached = localStorage.getItem(COGNITO_TOKEN_KEY)
      if (cached) {
        const payload = decodeJwtPayload(cached)
        if (
          payload &&
          typeof (payload as { exp?: number }).exp === 'number' &&
          (payload as { exp: number }).exp * 1000 > Date.now()
        ) {
          if (!cancelled) {
            setUser(userFromPayload(payload as Record<string, unknown>))
            setToken(cached)
            setLoading(false)
            releasedApp = true
          }
        } else {
          localStorage.removeItem(COGNITO_TOKEN_KEY)
        }
      }

      try {
        const enriched = await enrichUserFromCognito()
        if (cancelled) return
        if (enriched) {
          setUser(enriched.user)
          setToken(enriched.token)
          if (enriched.token) localStorage.setItem(COGNITO_TOKEN_KEY, enriched.token)
        } else if (!releasedApp) {
          setUser(null)
          setToken(null)
        }
      } catch {
        cognitoSignOut()
        localStorage.removeItem(COGNITO_TOKEN_KEY)
        if (!cancelled) {
          setUser(null)
          setToken(null)
        }
      } finally {
        if (!cancelled) {
          if (!releasedApp) setLoading(false)
          setUserProfileLoading(false)
        }
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [])

  const completeLoginProcess = useCallback(async (username: string, attributes: Record<string, string>) => {
    setUserProfileLoading(true)
    try {
      const jwtToken = await getJwtToken()
      const userData = userFromPayload(attributes as Record<string, unknown>, username)
      setUser(userData)
      setToken(jwtToken)
      if (jwtToken) localStorage.setItem(COGNITO_TOKEN_KEY, jwtToken)
    } finally {
      setUserProfileLoading(false)
    }
  }, [])

  const login = useCallback(
    async (username: string, password: string): Promise<{ success: boolean; mfaRequired?: boolean }> => {
      setLoading(true)
      try {
        const { attributes } = await cognitoSignIn(username, password)
        const email = (attributes.email as string) || username

        try {
          const mfaRes = await getMfaStatus(email.trim())
          if (mfaRes.requireOtp) {
            await sendMfaOtp(email.trim())
            const pending = { username, attributes: attributes as Record<string, string> }
            persistMfaPending(email.trim(), pending)
            setMfaSession(email.trim())
            setMfaRequired(true)
            setPendingUser(pending)
            return { success: true, mfaRequired: true }
          }
        } catch (err) {
          console.warn('MFA check failed or unreachable, proceeding with login', err)
        }

        await completeLoginProcess(username, attributes as Record<string, string>)
        return { success: true, mfaRequired: false }
      } finally {
        setLoading(false)
      }
    },
    [completeLoginProcess]
  )

  const completeMfaSignIn = useCallback(
    async (otp: string): Promise<{ success: boolean; error?: string }> => {
      if (!mfaSession || !pendingUser) {
        return { success: false, error: 'No active MFA session' }
      }

      try {
        setLoading(true)
        await verifyMfaOtp(mfaSession, otp, mfaSession)
        await completeLoginProcess(pendingUser.username, pendingUser.attributes)
        clearMfaPending()
        setMfaRequired(false)
        setMfaSession(null)
        setPendingUser(null)
        return { success: true }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Invalid or expired OTP code.'
        return { success: false, error: message }
      } finally {
        setLoading(false)
      }
    },
    [mfaSession, pendingUser, completeLoginProcess]
  )

  const resendMfaOtp = useCallback(async (): Promise<void> => {
    if (!mfaSession) throw new Error('No active MFA session')
    await sendMfaOtp(mfaSession)
  }, [mfaSession])

  const cancelMfa = useCallback(() => {
    clearMfaPending()
    cognitoSignOut()
    setMfaRequired(false)
    setMfaSession(null)
    setPendingUser(null)
    setUser(null)
    setToken(null)
    setUserProfileLoading(false)
    localStorage.removeItem(COGNITO_TOKEN_KEY)
  }, [])

  const logout = useCallback(() => {
    clearMfaPending()
    cognitoSignOut()
    setUser(null)
    setToken(null)
    setMfaRequired(false)
    setMfaSession(null)
    setPendingUser(null)
    setUserProfileLoading(false)
    localStorage.removeItem(COGNITO_TOKEN_KEY)
  }, [])

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!user && !!token && !mfaRequired,
    loading,
    userProfileLoading,
    mfaRequired,
    completeMfaSignIn,
    resendMfaOtp,
    cancelMfa
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext)
  if (ctx === undefined) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
